import { useState, useEffect, useCallback, useRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

import { useToast } from "../../hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import PTSNavigationRoutes from "../../utils/navigationRoutes";
import PTSLogo from "../../assets/us-logo.png";
import {
  PtsEmployeeInviteService,
  AuthService,
  fetchPersistAndCacheCurrentUserDetails,
  getCurrentUserDisplayName,
  normalizeCurrentUserEmployee,
  UserRoleService,
  UploadService,
} from "@/services/APIs";
import {
  setLocalUserInfo,
  setLocalUserRoles,
  setLocalZulipAuth,
} from "@/lib/storeManager";
import bgImage from "@/project/PTS/assets/pts-bg.jpg";
import {
  validatePassword,
  getPasswordRequirementsText,
  type PasswordValidationResult,
} from "../../utils/passwordValidation";
import { notify } from "@/components/common/Notification";

export default function Auth() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailName, setEmailName] = useState(""); // For signup: only the name part before @
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] =
    useState<PasswordValidationResult | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [verifyingInvite, setVerifyingInvite] = useState(false);
  const [inviteData, setInviteData] = useState<{
    employee: string;
    user_id: string;
  } | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verifiedCodeRef = useRef<string | null>(null);

  // Verify invite code automatically
  const verifyInviteCode = useCallback(
    async (inviteCode: string) => {
      // Prevent duplicate calls for the same code
      if (verifiedCodeRef.current === inviteCode) {
        return;
      }
      verifiedCodeRef.current = inviteCode;
      setVerifyingInvite(true);
      try {
        const response =
          await PtsEmployeeInviteService.verifyInviteCode(inviteCode);
        if (response) {
          setInviteData({
            employee: response.data.employee_id,
            user_id: "",
          });
          toast({
            title: "Success",
            description: "Invite code verified successfully!",
          });
        }
      } catch (error: any) {
        console.error("Invite code verification failed:", error);
        verifiedCodeRef.current = null; // Reset on error so it can be retried
        toast({
          title: "Error",
          description:
            error?.message ??
            "Failed to verify invite code. Please check the code and try again.",
          variant: "destructive",
        });
      } finally {
        setVerifyingInvite(false);
      }
    },
    [toast]
  );
  // Auto-verify invite code when component loads (only once)
  useEffect(() => {
    const referCode = searchParams.get("refer_code");
    if (referCode && verifiedCodeRef.current !== referCode) {
      verifyInviteCode(referCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await AuthService.login({
        email: email,
        password: password,
      });

      if (response) {
        // Store tokens
        localStorage.setItem("accessToken", response.access);
        localStorage.setItem("refreshToken", response.refresh);

        // Store user info
        if (response.user) {
          let employeeImageUrl = null;
          let currentUserDetails = null;
          try {
            currentUserDetails =
              await fetchPersistAndCacheCurrentUserDetails(queryClient);
            const employeeData =
              normalizeCurrentUserEmployee(currentUserDetails);
            if (employeeData?.img) {
              try {
                const imageData = await UploadService.getFileById({
                  id: employeeData.img,
                });
                employeeImageUrl =
                  imageData?.file_path_thumbnail ??
                  imageData?.file_path ??
                  null;
              } catch (imageError: any) {
                console.error("Failed to fetch employee image:", imageError);
              }
            }
          } catch (employeeError: any) {
            console.error(
              "Failed to fetch current user details:",
              employeeError
            );
          }

          const apiDisplayName = getCurrentUserDisplayName(currentUserDetails);

          setLocalUserInfo({
            first_name: response.user.first_name,
            last_name: response.user.last_name,
            email: response.user.email,
            user_type: response.user.user_type,
            full_name:
              apiDisplayName ??
              response.user.full_name ??
              `${response.user.first_name} ${response.user.last_name}`.trim(),
            id: response.user.id,
            ...(currentUserDetails?.initials && {
              initials: currentUserDetails.initials,
            }),
            ...(employeeImageUrl && { img: employeeImageUrl }),
          });

          // Fetch and store user roles after login
          try {
            const roleData = await UserRoleService.getUserCurrentRole(
              response.user.id
            );
            if (roleData?.roles) {
              setLocalUserRoles(roleData.roles);
              console.log("User roles stored:", roleData.roles);
            }
          } catch (roleError: any) {
            console.error("Failed to fetch user roles:", roleError);
            // Don't block login if role fetch fails
          }

          // Fetch and store Zulip auth token after successful login
          try {
            const zulipAuth = await AuthService.getZulipToken();
            if (zulipAuth) {
              setLocalZulipAuth(zulipAuth);
            }
          } catch (zulipError: any) {
            console.warn("Failed to fetch Zulip auth token:", zulipError);
          }
        }

        toast({
          title: "Success",
          description: "Login successful!",
        });
        navigate(PTSNavigationRoutes.EMPLOYEE_DASHBOARD);
      }
    } catch (error: any) {
      console.error("Login failed:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message ??
          error?.message ??
          "Login failed. Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullEmail = emailName ? `${emailName}` : "";

    if (!fullEmail || !password) {
      toast({
        title: "Error",
        description: "Please fill in all required fields correctly.",
        variant: "destructive",
      });
      return;
    }

    // Comprehensive password validation
    const validation = validatePassword(password, {
      username: emailName || undefined,
      email: fullEmail || undefined,
    });

    setPasswordValidation(validation);

    if (!validation.isValid) {
      toast({
        title: "Password Validation Error",
        description:
          validation.errors[0] || "Password does not meet requirements",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const referCode = searchParams.get("refer_code");

      // Step 1: Always check email via checkSignupEmail API first (works with or without refer_code)
      try {
        const emailCheckPayload: { email: string; refer_code?: string } = {
          email: fullEmail,
        };
        if (referCode) {
          emailCheckPayload.refer_code = referCode;
        }
        const emailCheckResponse =
          await PtsEmployeeInviteService.checkSignupEmail(emailCheckPayload);

        // Extract is_active from nested data structure or direct response
        const isActive =
          emailCheckResponse?.data?.is_active ?? emailCheckResponse?.is_active;
        const errorMessage =
          emailCheckResponse?.data?.message ?? emailCheckResponse?.message;

        // If email is not active (is_active === false), show error toast and stop signup
        if (isActive === false) {
          notify.error(errorMessage ?? "Email is not match");
          setLoading(false);
          return;
        }

        // Only proceed to signup if is_active is true
        if (isActive !== true) {
          notify.error(
            errorMessage ?? "Email verification failed. Please try again."
          );
          setLoading(false);
          return;
        }

        // Email check passed (is_active === true), proceed with signup
      } catch (emailCheckError: any) {
        console.error("Email check failed:", emailCheckError);
        toast({
          title: "Error",
          description:
            emailCheckError?.response?.data?.message ??
            emailCheckError?.message ??
            "Failed to verify email. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Step 2: Sign up user - only pass email and password (only reached if is_active === true)
      const response = await AuthService.signup({
        email: fullEmail,
        password: password,
      });

      if (response) {
        // Step 1: Auth (Signup) - Already completed above
        // Store tokens
        localStorage.setItem("accessToken", response.access);
        localStorage.setItem("refreshToken", response.refresh);

        // Store user info
        if (response.user) {
          setLocalUserInfo({
            first_name: response.user.first_name,
            last_name: response.user.last_name,
            email: response.user.email,
            user_type: response.user.user_type,
            full_name:
              response.user.full_name ??
              `${response.user.first_name} ${response.user.last_name}`,
            id: response.user.id,
          });

          // Step 2: Call user-current-role API
          try {
            const roleData = await UserRoleService.getUserCurrentRole(
              response.user.id
            );
            if (roleData?.roles) {
              setLocalUserRoles(roleData.roles);
              console.log("User roles stored:", roleData.roles);
            }
          } catch (roleError: any) {
            console.error("Failed to fetch user roles:", roleError);
            // Don't block signup if role fetch fails
          }

          // Step 3: Call bind-employee API (if inviteData is available)
          console.log(response, "response-----------");
          console.log(inviteData, "inviteData-----------");
          if (inviteData) {
            try {
              await PtsEmployeeInviteService.bindEmployee(
                inviteData.employee,
                response.user.id
              );
              console.log("Employee bound successfully");

              // Step 4: After bind API succeeds, fetch current user employee details
              try {
                const details =
                  await fetchPersistAndCacheCurrentUserDetails(queryClient);
                console.log("Current user details fetched:", details);

                if (details?.employee_id) {
                  const apiDisplayName = getCurrentUserDisplayName(details);
                  setLocalUserInfo({
                    first_name: response.user.first_name,
                    last_name: response.user.last_name,
                    email: response.user.email,
                    user_type: response.user.user_type,
                    full_name:
                      apiDisplayName ??
                      response.user.full_name ??
                      `${response.user.first_name} ${response.user.last_name}`.trim(),
                    id: response.user.id,
                    ...(details.initials && { initials: details.initials }),
                  });
                }

                // Old API: PTS Emp schema with users__eq filter
                // const ptsEmpData = await DynamicModuleService.getItemsByParams(
                //   "PTS Emp",
                //   { users__eq: response.user.id }
                // );
                // if (ptsEmpData && Array.isArray(ptsEmpData) && ptsEmpData.length > 0) {
                //   const employeeId =
                //     ptsEmpData[0]?.id ?? ptsEmpData[0]?.ID ?? ptsEmpData[0]?._id;
                //   if (employeeId) {
                //     setLocalEmployeeId(employeeId);
                //   }
                // }
              } catch (ptsEmpError: any) {
                console.error(
                  "Failed to fetch current user details:",
                  ptsEmpError
                );
              }

              toast({
                title: "Success",
                description: "Account created and employee bound successfully!",
              });
            } catch (bindError: any) {
              console.error("Employee binding failed:", bindError);
              toast({
                title: "Error",
                description:
                  "Failed to bind employee. Proceeding to dashboard.",
                variant: "destructive",
              });
            }
          } else {
            // If no inviteData, try to fetch employee ID using fallback method
            try {
              const details =
                await fetchPersistAndCacheCurrentUserDetails(queryClient);
              if (details?.employee_id) {
                const apiDisplayName = getCurrentUserDisplayName(details);
                setLocalUserInfo({
                  first_name: response.user.first_name,
                  last_name: response.user.last_name,
                  email: response.user.email,
                  user_type: response.user.user_type,
                  full_name:
                    apiDisplayName ??
                    response.user.full_name ??
                    `${response.user.first_name} ${response.user.last_name}`.trim(),
                  id: response.user.id,
                  ...(details.initials && { initials: details.initials }),
                });
              }
            } catch (employeeError: any) {
              console.error("Failed to fetch employee ID:", employeeError);
              // Don't block signup if employee fetch fails
            }

            toast({
              title: "Success",
              description:
                "Account created successfully without employee binding.",
            });
          }
        }

        // Redirect to dashboard after successful signup
        navigate(PTSNavigationRoutes.EMPLOYEE_DASHBOARD);
      }
    } catch (error: any) {
      console.error("Sign up failed:", error);

      // Handle specific error cases
      const errorMessage =
        error?.response?.data?.email?.[0] ??
        error?.response?.data?.message ??
        error?.message ??
        "Failed to create account. Please try again.";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      toast({
        title: "Error",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setForgotPasswordLoading(true);
    try {
      const response = await AuthService.forgotPassword({
        email: forgotPasswordEmail,
        is_pts: true,
      });

      if (response) {
        notify.success("Password reset link has been sent to your email.");
        // Navigate back to signin form
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
      }
    } catch (error: any) {
      console.error("Forgot password failed:", error);
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to send reset link. Please try again."
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-gradient-to-br bg-cover bg-center bg-no-repeat p-4"
      style={{
        backgroundImage: `
        linear-gradient(to bottom right,
          rgba(0, 0, 0, 0.2),
          rgba(0, 0, 0, 0.2)
        ),
        url(${bgImage})
      `,
      }}
    >
      <Card className="w-full max-w-md bg-white/90">
        <CardHeader className="text-center">
          <img src={PTSLogo} alt="PTS Logo" className="mx-auto mb-4 w-24" />
          {/* <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary">
            <span className="text-lg font-bold text-primary-foreground">
              P
            </span>
          </div> */}
          <CardTitle className="text-2xl">Project Tracking System</CardTitle>
          <CardDescription>Welcome to PTS</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            {!showForgotPassword && (
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="signin">
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-password-email">Email</Label>
                    <Input
                      id="forgot-password-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotPasswordEmail}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setForgotPasswordEmail(e.target.value)
                      }
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={forgotPasswordLoading}
                  >
                    {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail("");
                    }}
                  >
                    Back to Sign In
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="signin-password"
                        type={showSignInPassword ? "text" : "password"}
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setPassword(e.target.value)
                        }
                        className="pr-10"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowSignInPassword(!showSignInPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        {!showSignInPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-primary text-sm hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </form>
              )}
            </TabsContent>

            <TabsContent value="signup">
              {verifyingInvite && (
                <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                  Verifying invite code...
                </div>
              )}
              {inviteData && (
                <div className="mb-4 rounded-md bg-green-50 p-3 text-sm text-green-800">
                  Invite code verified successfully!
                </div>
              )}
              <form onSubmit={handleSignUp} className="space-y-4">
                {/* <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFullName(e.target.value)
                    }
                    required
                  />
                </div> */}
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Work Email</Label>
                  <div className="flex items-center">
                    <Input
                      id="signup-email"
                      type="text"
                      placeholder="john.doe"
                      value={emailName}
                      // onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      //   const value = e.target.value.replace(
                      //     /[^a-zA-Z0-9]/g,
                      //     ""
                      //   );
                      //   setEmailName(value);
                      // }}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        // Only allow characters that are valid for email username (no @ symbol)
                        // const value = e.target.value.replace(/@/g, "");
                        setEmailName(e.target.value);
                      }}
                      className="w-full"
                      required
                    />
                    {/* <div className="border-input bg-muted text-muted-foreground flex h-10 w-1/2 items-center justify-center rounded-r-md border border-l-0 px-3 text-sm">
                      @universal-software.com
                    </div> */}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signup-password">Password</Label>
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswordRequirements(!showPasswordRequirements)
                      }
                      className="text-primary text-xs hover:underline"
                    >
                      {showPasswordRequirements
                        ? "Hide Requirements"
                        : "Show Requirements"}
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showSignUpPassword ? "text" : "password"}
                      placeholder="Password (8-10 characters)"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newPassword = e.target.value;
                        setPassword(newPassword);
                        if (newPassword) {
                          const validation = validatePassword(newPassword, {
                            username: emailName || undefined,
                            email: emailName ? `${emailName}` : undefined,
                          });
                          setPasswordValidation(validation);
                        } else {
                          setPasswordValidation(null);
                        }
                      }}
                      className={`pr-10 ${
                        passwordValidation && !passwordValidation.isValid
                          ? "border-red-500 focus-visible:ring-red-500"
                          : passwordValidation?.isValid
                            ? "border-green-500 focus-visible:ring-green-500"
                            : ""
                      }`}
                      required
                      minLength={8}
                      maxLength={10}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {!showSignUpPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordValidation &&
                    passwordValidation.errors.length > 0 && (
                      <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">
                        <ul className="list-disc space-y-1 pl-4">
                          {passwordValidation.errors.slice(0, 3).map(error => (
                            <li key={error}>{error}</li>
                          ))}
                          {passwordValidation.errors.length > 3 && (
                            <li>
                              ...and {passwordValidation.errors.length - 3} more
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  {passwordValidation &&
                    passwordValidation.isValid &&
                    password.length >= 8 && (
                      <div className="rounded-md bg-green-50 p-2 text-xs text-green-800">
                        ✓ Password meets all requirements
                      </div>
                    )}
                  {showPasswordRequirements && (
                    <div className="rounded-md bg-blue-50 p-3 text-xs text-blue-900">
                      <p className="mb-2 font-semibold">
                        Password Requirements:
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        {getPasswordRequirementsText().map(req => (
                          <li key={req}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Creating Account..." : "Sign Up"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
